function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import compact from 'lodash/compact';
import { Callout, ConfirmationModal, POPOVER_ARROW_ICON_WIDTH, PopoverArrowIcon, TextButton, Typography } from 'seagull';
import { BankLine, CardLine, Plus } from 'seagull/icons';
import PaymentMethodOption from "./PaymentMethodOption";
import styles from "./ManagePaymentMethods.module.scss";
import { getReadonlyWarning } from "./getReadonlyWarning";
import ConnectPlaid from "./ConnectPlaid";
import ConnectCreditCard from "./ConnectCreditCard";
import RestfulWrapper from "../RestfulWrapper/RestfulWrapper";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import BankAccountPanel from "./BankAccountPanel";
import CreditCardPanel from "./CreditCardPanel";
import { selectors } from "../../store/state-selectors";
import { useRemovePaymentMethod } from "../../hooks/useRemovePaymentMethod";
import { BusinessChannelEventBinder, PaymentMethodEventType, PusherEvent } from "../../pusher";
import { PaymentMethodPanelSkeleton } from "./PaymentMethodPanel";
import LoadingIndicator from "../LoadingIndicator";
import { handlePromise } from "../../infra/utils";
import { paymentMethodModel } from "../../models";
import { useOnMount } from "../../hooks/useOnMount";
import { Intent } from "../../infra/access-control";
import InviteOrEditMemberModal from "../InviteOrEditMemberModal";
import { doPaymentMethodAction as _doPaymentMethodAction, fetchPaymentMethods, getRequiresPMActionConfirmation, isSelectablePaymentMethod } from "./ManagePaymentMethods.utils";
import { useDisconnectPaymentMethod } from "../../hooks/useDisconnectPaymentMethod";
import { RelationshipProgressStatus } from "../../models/relationship";
const addAnotherBtnText = 'Add another payment method';
var SelectPaymentMethodOption = /*#__PURE__*/function (SelectPaymentMethodOption) {
  SelectPaymentMethodOption["Plaid"] = "plaid";
  SelectPaymentMethodOption["CreditCard"] = "creditCard";
  return SelectPaymentMethodOption;
}(SelectPaymentMethodOption || {});
export function ActualManagePaymentMethods(props) {
  const {
    vendorId,
    customerId,
    relationshipId,
    isPreview,
    isMemberClient,
    isSample,
    showCreditCardFee,
    selectedFundingSourceId,
    isMobile,
    isVendorCoveringFees,
    isProposal = false,
    isRefreshing,
    isTerminatedAgreement = false,
    isInviteMemberDisabled,
    hideInviteMemberHint = false,
    isAddingAnotherPaymentMethod,
    hideExpandableAddPmButton,
    refresh,
    onSelectPaymentMethod,
    onPaymentMethodUpdated,
    setIsAddingAnotherPaymentMethod
  } = props;
  const {
    apiGatewayHost
  } = useStore(state => state.session);
  const isVendor = useStore(selectors.user.isVendor);
  const userRole = useStore(selectors.user.role);
  const canVendorControlPM = isVendor && !isProposal;
  const requiresPMActionConfirmation = getRequiresPMActionConfirmation(isVendor, userRole, isProposal);
  const {
    removePaymentMethodWithConfirmation,
    removePaymentMethod
  } = useRemovePaymentMethod();
  const disconnectPaymentMethod = useDisconnectPaymentMethod();
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const [arrowLeft, setArrowLeft] = useState(0);
  const [achButtonEl, setAchButtonEl] = useState(null);
  const [ccButtonEl, setCcButtonEl] = useState(null);
  const [selectedOption, setSelectedOption] = useState(SelectPaymentMethodOption.CreditCard);
  const [paymentMethods, setPaymentMethods] = useState(props.paymentMethods);
  useEffect(() => setPaymentMethods(props.paymentMethods), [props.paymentMethods]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(() => {
    if (isProposal) {
      var _paymentMethods$;
      return selectedFundingSourceId || ((_paymentMethods$ = paymentMethods[0]) === null || _paymentMethods$ === void 0 ? void 0 : _paymentMethods$.id) || null;
    } else {
      return selectedFundingSourceId || null;
    }
  });
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(paymentMethods.length === 0);
  const [fundingSourceIdsAdded, setFundingSourceIdsAdded] = useState([]);
  const selectedPM = paymentMethods.find(pm => pm.id === selectedPaymentMethod);
  const selectedPMRequiresVerification = !!selectedPM && paymentMethodModel.requiresVerification(selectedPM);
  useOnMount(() => {
    if (selectedPaymentMethod && isProposal) {
      onSelectPaymentMethod && onSelectPaymentMethod(selectedPM);
    } else {
      onSelectPaymentMethod && onSelectPaymentMethod();
    }
  });
  useEffect(() => {
    if (isAddingAnotherPaymentMethod) {
      setIsAddingPaymentMethod(isAddingAnotherPaymentMethod);
    }
  }, [isAddingAnotherPaymentMethod]);
  useEffect(() => {
    const applyNewArrowLeft = buttonEl => {
      const {
        offsetLeft,
        clientWidth
      } = buttonEl;
      const arrowLeft = offsetLeft + clientWidth / 2 - POPOVER_ARROW_ICON_WIDTH / 2;
      setArrowLeft(arrowLeft);
    };
    if (selectedOption === SelectPaymentMethodOption.Plaid && achButtonEl) {
      applyNewArrowLeft(achButtonEl);
    } else if (selectedOption === SelectPaymentMethodOption.CreditCard && ccButtonEl) {
      applyNewArrowLeft(ccButtonEl);
    }
  }, [selectedOption, achButtonEl, ccButtonEl]);
  useEffect(() => {
    if (paymentMethods.length === 0) {
      // This will happen after removing the only payment-method.
      setIsAddingPaymentMethod(true);
    }
  }, [paymentMethods]);
  const disabledReason = getReadonlyWarning({
    isPreview,
    isMemberClient,
    isSampleRelationship: isSample
  });
  const sortedPaymentMethods = useMemo(() => {
    return paymentMethods.slice().sort((pmA, pmB) => {
      if (!selectedPaymentMethod || isProposal) return 0;
      if (pmB.id === selectedPaymentMethod) return 1;
      if (pmA.id === selectedPaymentMethod) return -1;
      return 0;
    });
  }, [paymentMethods, selectedPaymentMethod, isProposal]);
  async function onPlaidSuccess(account, paymentMethodAction) {
    const fundingSourceId = account.id;
    if (isProposal) {
      api.relationship.updateProgressStatus(relationshipId, RelationshipProgressStatus.PaymentMethod);
    }
    logBI(10807);
    if (paymentMethodAction.type === 'replace') {
      const pmToReplace = paymentMethods.find(pm => pm.id === paymentMethodAction.fundingSourceId);
      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodAction.fundingSourceId));
      removePaymentMethod({
        fundingSourceId: paymentMethodAction.fundingSourceId,
        vendorId,
        customerId
      }).then(result => {
        if (!result.success && pmToReplace) {
          setPaymentMethods(prev => [...prev, pmToReplace]);
          overlayContext.showErrorToast('Failed to remove the existing payment method. Please remove it using the 3 dots menu', 'failed-remove-pm-toast');
        }
      });
    }
    setIsAddingPaymentMethod(false);
    setIsAddingAnotherPaymentMethod && setIsAddingAnotherPaymentMethod(false);
    setFundingSourceIdsAdded(fundingSourceIdsAdded.concat(fundingSourceId));
    // The rest of the flow is done after receiving a paymentMethodEvent in the subscriber callback below.
  }
  async function onConnectCreditCard(fundingSourceId) {
    setIsAddingPaymentMethod(false);
    setIsAddingAnotherPaymentMethod && setIsAddingAnotherPaymentMethod(false);
    const {
      paymentMethods
    } = await refresh();
    const pm = paymentMethods.find(paymentMethod => paymentMethod.id === fundingSourceId);
    if (pm && shouldSelectAfterAddingPM(paymentMethods)) {
      _onSelect(pm);
    }
  }
  async function _onSelect(paymentMethod) {
    if (isProposal) {
      setSelectedPaymentMethod(paymentMethod.id);
      onSelectPaymentMethod && onSelectPaymentMethod(paymentMethod);
    } else {
      overlayContext.startLoader();
      const {
        success
      } = await handlePromise(api.relationship.changePaymentMethod(relationshipId, paymentMethod.id));
      if (!success) {
        overlayContext.showErrorToast('Failed to connect payment method');
      } else {
        await refresh();
        setSelectedPaymentMethod(paymentMethod.id);
      }
      overlayContext.stopLoader();
    }
  }
  function openOnBehalfModal(onConfirmation, paymentMethod) {
    const action = paymentMethod ? 'set' : 'add';
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          title: "".concat(paymentMethod ? 'Setting' : 'Adding', " a payment method on behalf of your client"),
          textAlignCenter: false,
          content: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, null, "By proceeding I confirm that I am authorized by my clients to ", action, " a payment method and initiate billing under the applicable agreement. Anchor is not a party to this agreement, and all responsibility for this authorization and related actions lies with the undersigned.")),
          onPrimaryButtonClick: async () => {
            onConfirmation();
            dismissModal(true);
          },
          onSecondaryButtonClick: () => dismissModal(false),
          primaryButtonLabel: "Proceed",
          secondaryButtonLabel: "Back",
          primaryButtonTestid: "proceed-to-".concat(action, "-PM"),
          secondaryButtonTestid: "cancel-".concat(action, "-PM"),
          "data-testid": "".concat(action, "-on-behalf-modal")
        });
      }
    });
  }
  function addAnotherPaymentMethod() {
    setSelectedOption(SelectPaymentMethodOption.CreditCard);
    setIsAddingPaymentMethod(true);
  }
  async function onPaymentMethodPushEvent(_ref2) {
    let {
      payload
    } = _ref2;
    const paymentMethod = payload.paymentMethod;
    if (payload.type === PaymentMethodEventType.PaymentMethodUpdated && onPaymentMethodUpdated) {
      onPaymentMethodUpdated();
    }
    const {
      paymentMethods: newPaymentMethods
    } = await refresh();
    if (payload.type === PaymentMethodEventType.FailedToAddPaymentMethod) {
      if (!paymentMethod) return;
      if (fundingSourceIdsAdded.some(id => id === paymentMethod.fundingSourceId)) {
        setFundingSourceIdsAdded(fundingSourceIdsAdded.filter(id => id !== paymentMethod.fundingSourceId));
        overlayContext.showErrorToast('The bank account you tried to add is already connected', 'add-duplicate-pm-toast');
      } else if (paymentMethods.some(pm => pm.id === paymentMethod.fundingSourceId && paymentMethodModel.isPendingManualVerification(pm))) {
        overlayContext.showErrorToast('The bank account you tried to verify is already connected', 'verify-duplicate-pm-toast');
      }
    } else if (payload.type === PaymentMethodEventType.PaymentMethodAdded && fundingSourceIdsAdded.length) {
      const addedPM = newPaymentMethods.find(pm => fundingSourceIdsAdded.includes(pm.id));
      if (addedPM) {
        if (shouldSelectAfterAddingPM(newPaymentMethods)) {
          _onSelect(addedPM);
        }
        setFundingSourceIdsAdded(fundingSourceIdsAdded.filter(id => addedPM.id !== id));
      }
    }
  }
  function shouldSelectAfterAddingPM(newPaymentMethods) {
    return newPaymentMethods.length === 1 || isProposal || selectedPMRequiresVerification;
  }
  const connectPaymentMethodProps = {
    relationshipId,
    vendorId,
    customerId: customerId,
    disabledReason,
    updateProgress: isProposal,
    doPaymentMethodAction: (action, paymentMethod) => _doPaymentMethodAction(action, requiresPMActionConfirmation, openOnBehalfModal, paymentMethod)
  };
  const connectAch = (isMobile || selectedOption === SelectPaymentMethodOption.Plaid) && /*#__PURE__*/React.createElement(ConnectPlaid, _extends({}, connectPaymentMethodProps, {
    onSuccess: onPlaidSuccess
  }));
  const connectCreditCard = (isMobile || selectedOption === SelectPaymentMethodOption.CreditCard) && /*#__PURE__*/React.createElement(ConnectCreditCard, _extends({}, connectPaymentMethodProps, {
    onConnect: onConnectCreditCard
  }));
  function openInviteMemberModal() {
    return overlayContext.showModal({
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/React.createElement(InviteOrEditMemberModal, {
          onClose: dismissModal
        });
      }
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BusinessChannelEventBinder, {
    eventName: PusherEvent.PaymentMethod,
    callback: onPaymentMethodPushEvent
  }), (!isRefreshing || paymentMethods.length > 0 || fundingSourceIdsAdded.length > 0) && /*#__PURE__*/React.createElement("div", {
    className: cn(styles.existingPaymentMethods, hideExpandableAddPmButton && styles.noAddPaymentTextButton)
  }, sortedPaymentMethods.map(paymentMethod => {
    const isSelected = paymentMethod.id === selectedPaymentMethod;
    const disconnectable = canVendorControlPM && isSelected;
    const commonProps = {
      key: paymentMethod.id,
      isProposal,
      isSelected,
      isMobile,
      apiGatewayHost,
      removable: !isTerminatedAgreement && (!isSelected || paymentMethodModel.failedVerification(paymentMethod)) && !isProposal && paymentMethod.isRemovable && !isMemberClient,
      selectable: isSelectablePaymentMethod(paymentMethod, isTerminatedAgreement, isSelected, isProposal, selectedPMRequiresVerification),
      disconnectable,
      verifyable: !isTerminatedAgreement,
      hasPermissionToVerify: !isMemberClient,
      onRemove: () => removePaymentMethodWithConfirmation({
        fundingSourceId: paymentMethod.id,
        vendorId,
        customerId: props.customerId,
        isCreditCard: paymentMethodModel.isCreditCard(paymentMethod),
        last4: paymentMethod.last4,
        async onSuccess() {
          await refresh();
          onPaymentMethodUpdated && onPaymentMethodUpdated();
        }
      }),
      onDisconnect: () => disconnectPaymentMethod({
        relationshipId,
        isCreditCard: paymentMethodModel.isCreditCard(paymentMethod),
        last4: paymentMethod.last4,
        async onSuccess() {
          setSelectedPaymentMethod(null);
        }
      }),
      onSelect: () => _doPaymentMethodAction(() => _onSelect(paymentMethod), requiresPMActionConfirmation, openOnBehalfModal, paymentMethod)
    };
    return paymentMethodModel.isBankAccount(paymentMethod) ? /*#__PURE__*/React.createElement(BankAccountPanel, _extends({}, commonProps, {
      bankAccount: paymentMethod,
      relationshipId: relationshipId,
      customerId: customerId,
      vendorId: vendorId,
      onSuccess: onPlaidSuccess
    })) : /*#__PURE__*/React.createElement(CreditCardPanel, _extends({}, commonProps, {
      creditCard: paymentMethod
    }));
  }), fundingSourceIdsAdded.map((_, idx) => /*#__PURE__*/React.createElement(PaymentMethodPanelSkeleton, {
    key: idx,
    isMobile: isMobile
  }))), !isTerminatedAgreement && paymentMethods.length > 0 && !hideExpandableAddPmButton && (isAddingPaymentMethod ? /*#__PURE__*/React.createElement(Typography, {
    className: styles.addPaymentMethod,
    skin: "secondary"
  }, addAnotherBtnText) : /*#__PURE__*/React.createElement(TextButton, {
    className: styles.addPaymentMethod,
    prefixIcon: /*#__PURE__*/React.createElement(Plus, null),
    "data-testid": "manage-payment-methods-add-payment-method",
    intent: Intent.AddPaymentMethod,
    onClick: addAnotherPaymentMethod
  }, addAnotherBtnText)), !isTerminatedAgreement && isAddingPaymentMethod && /*#__PURE__*/React.createElement("div", {
    className: cn(styles.options, {
      [styles.isMobile]: isMobile,
      [styles.ccNoFee]: !showCreditCardFee
    })
  }, /*#__PURE__*/React.createElement(PaymentMethodOption, {
    text: "Credit card",
    parenthesisText: canVendorControlPM ? "fee paid by ".concat(isVendorCoveringFees ? 'you' : 'client') : showCreditCardFee ? '2.9%+30¢ fee' : 'free',
    icon: /*#__PURE__*/React.createElement(CardLine, null),
    selected: selectedOption === SelectPaymentMethodOption.CreditCard,
    "data-testid": "manage-payment-methods-credit-card",
    ref: setCcButtonEl,
    onOptionClick: () => setSelectedOption(SelectPaymentMethodOption.CreditCard),
    mobileView: isMobile
  }, connectCreditCard), /*#__PURE__*/React.createElement(PaymentMethodOption, {
    text: "Secure ACH",
    parenthesisText: canVendorControlPM ? '' : 'free',
    icon: /*#__PURE__*/React.createElement(BankLine, null),
    selected: selectedOption === SelectPaymentMethodOption.Plaid,
    "data-testid": "manage-payment-methods-secure-ach",
    ref: setAchButtonEl,
    onOptionClick: () => setSelectedOption(SelectPaymentMethodOption.Plaid),
    mobileView: isMobile
  }, connectAch), !isMobile && /*#__PURE__*/React.createElement("div", {
    className: styles.box
  }, /*#__PURE__*/React.createElement(PopoverArrowIcon, {
    className: styles.arrow,
    style: {
      left: arrowLeft
    }
  }), compact([connectAch, connectCreditCard]))), sortedPaymentMethods.length === 0 && !hideInviteMemberHint && /*#__PURE__*/React.createElement("div", {
    className: cn(styles.inviteMemberCallout, {
      [styles.isMobile]: isMobile
    })
  }, /*#__PURE__*/React.createElement(Callout, null, "Do you need help from a team member to add a payment method?", ' ', /*#__PURE__*/React.createElement(TextButton, {
    intent: Intent.InviteTeamMember,
    disabled: isInviteMemberDisabled,
    onClick: openInviteMemberModal,
    "data-testid": "invite-member"
  }, "Invite a team member"))));
}
export default function ManagePaymentMethods(props) {
  const {
    isPreview,
    isSample,
    relationshipId,
    isProposal,
    vendorId,
    customerId,
    initialSelectedPaymentMethodId
  } = props;
  const {
    api
  } = useStoreActions();
  const businessId = useStore(selectors.user.businessId);
  const fetch = () => {
    return fetchPaymentMethods({
      isSample,
      vendorId,
      isPreview,
      businessId,
      isProposal,
      initialSelectedPaymentMethodId,
      clientGetPaymentMethod: () => api.paymentMethod.clientGet(vendorId),
      vendorGetPaymentMethod: () => api.paymentMethod.vendorGet(customerId),
      getSelectedPaymentMethod: () => api.relationship.getSelectedPaymentMethod(relationshipId)
    });
  };
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: fetch,
    renderChild: _ref4 => {
      let {
        data,
        loading,
        refresh
      } = _ref4;
      return /*#__PURE__*/React.createElement(ActualManagePaymentMethods, _extends({}, props, {
        paymentMethods: data.paymentMethods,
        selectedFundingSourceId: data.selectedFundingSourceId,
        isRefreshing: loading,
        refresh: refresh
      }));
    },
    renderLoader: () => /*#__PURE__*/React.createElement(LoadingIndicator, {
      testid: "manage-payment-methods-loading-indicator"
    })
  });
}